import type { TaskResult } from "../types";

export async function gradeProject6(): Promise<TaskResult[]> {
  const results: TaskResult[] = [];

  // Tarea 1: Subtotales en la hoja Ventas
  // Verificar que existen filas con SUBTOTAL en columna K (Precio extendido)
  // y que los datos están ordenados por Nombre del producto (col H)
  await Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem("Ventas");
    // Check for SUBTOTAL formulas in column K
    const rangoK = ws.getRange("K1:K400");
    rangoK.load("formulas,rowCount");
    await context.sync();

    const rangoH = ws.getRange("H1:H400");
    rangoH.load("values");
    await context.sync();

    // Collect unique product names (non-total data rows) and total labels
    const productNames: string[] = [];
    const uniqueProducts = new Set<string>();
    const totalLabels: string[] = [];
    let tieneSubtotal = false;

    for (let i = 1; i < rangoH.values.length; i++) {
      const val = (rangoH.values[i][0] || "").toString();
      const valLower = val.toLowerCase();
      const formula = (rangoK.formulas[i]?.[0] || "").toString().toUpperCase();

      if (formula.includes("SUBTOTAL") || formula.includes("SUBTOTALES")) {
        tieneSubtotal = true;
      }

      // Skip grand total rows (English, Spanish, and other formats)
      if (
        val === "" ||
        valLower.startsWith("gran total") ||
        valLower.startsWith("grand total") ||
        valLower.startsWith("total general")
      ) continue;

      // Spanish format: "Total Alaska King Crab" → "alaska king crab"
      if (valLower.startsWith("total ")) {
        totalLabels.push(valLower.substring(6).trim());
        continue;
      }
      // English format: "Alaska King Crab Total" → "alaska king crab"
      if (valLower.endsWith(" total")) {
        totalLabels.push(valLower.substring(0, valLower.length - 6).trim());
        continue;
      }

      productNames.push(valLower);
      uniqueProducts.add(valLower);
    }

    // Check that data is sorted by product name
    let ordenado = true;
    for (let i = 1; i < productNames.length; i++) {
      if (productNames[i - 1] > productNames[i]) {
        ordenado = false;
        break;
      }
    }

    // Check that "Total X" labels match actual product names, not vendor names
    // At least one total label must exist and ALL must match a known product
    const subtotalPorProducto = totalLabels.length > 0 &&
      totalLabels.every((label) => uniqueProducts.has(label));

    results.push({ taskId: 1, passed: tieneSubtotal && ordenado && subtotalPorProducto });
  });

  // Tarea 2: Día de la semana en Envíos (DIASEM/WEEKDAY) con argumento 3
  // DIASEM(fecha;3) → lunes=0 … domingo=6
  await Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem("Envíos");
    const celdaH2 = ws.getRange("H2");
    const celdaH3 = ws.getRange("H3");
    celdaH2.load("formulas,values");
    celdaH3.load("formulas,values");
    await context.sync();

    const formulaH2 = (celdaH2.formulas[0][0] || "").toString().toUpperCase();
    const tieneFormula =
      formulaH2.includes("WEEKDAY") || formulaH2.includes("DIASEM");

    // Verify return_type argument is 3 (Mon=0..Sun=6)
    // Matches patterns like WEEKDAY(x,3) or DIASEM(x;3)
    const tieneArg3 = /(?:WEEKDAY|DIASEM)\s*\([^)]*[,;]\s*3\s*\)/.test(formulaH2);

    // With return_type=3, values range from 0 (Mon) to 6 (Sun)
    const valorH2 = Number(celdaH2.values[0][0]);
    const valorEnRango = valorH2 >= 0 && valorH2 <= 6;

    // Verificar que se aplicó a más de una celda
    const formulaH3 = (celdaH3.formulas[0][0] || "").toString().toUpperCase();
    const h3TieneFormula =
      formulaH3.includes("WEEKDAY") || formulaH3.includes("DIASEM");

    results.push({
      taskId: 2,
      passed: tieneFormula && tieneArg3 && valorEnRango && h3TieneFormula,
    });
  });

  // Tarea 3: Pago mensual en Extensión de almacen (PMT/PAGO)
  // =PAGO(B5/12;B6*12;B4;0;1) — type=1 means payment at beginning of period
  // Expected result: ~$2,830.48
  await Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem("Extensión de almacen");
    const celda = ws.getRange("B7");
    celda.load("formulas,values");
    await context.sync();

    const formula = (celda.formulas[0][0] || "").toString().toUpperCase();
    const tienePMT =
      formula.includes("PMT") || formula.includes("PAGO");

    // Verify type argument = 1 (beginning of period)
    // Matches: PMT(x,y,z,0,1) or PAGO(x;y;z;0;1)
    const tieneTipo1 = /(?:PMT|PAGO)\s*\([^)]*[,;]\s*1\s*\)/.test(formula);

    // Expected value: ~$2,830.48 (negative because it's a payment)
    const valor = Math.abs(Number(celda.values[0][0]));
    const valorCorrecto = valor > 2820 && valor < 2840;

    results.push({ taskId: 3, passed: tienePMT && tieneTipo1 && valorCorrecto });
  });

  // Tarea 4: Tabla dinámica con sub-agrupación (País primero, Nombre del producto segundo)
  await Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem("Resumen gráfico");
    const pivotTables = ws.pivotTables;
    pivotTables.load("items");
    await context.sync();

    let passed = false;
    for (const pt of pivotTables.items) {
      const rowHierarchies = pt.rowHierarchies;
      rowHierarchies.load("items/name");
      await context.sync();

      if (rowHierarchies.items.length >= 2) {
        const first = rowHierarchies.items[0].name.toLowerCase();
        const second = rowHierarchies.items[1].name.toLowerCase();
        // País must be first, Nombre del producto second
        if (
          first.includes("país") || first.includes("pais") || first.includes("country")
        ) {
          if (
            second.includes("producto") || second.includes("product")
          ) {
            passed = true;
          }
        }
        break;
      }
    }

    results.push({ taskId: 4, passed });
  });

  // Tarea 5: Estilo de gráfico = 4
  // Excel UI "Style 4" maps to chart.style = 204 in the API
  await Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem("Resumen gráfico");
    const charts = ws.charts;
    charts.load("items");
    await context.sync();

    let passed = false;
    for (const chart of charts.items) {
      chart.load("style");
      await context.sync();

      if (chart.style === 204) {
        passed = true;
        break;
      }
    }

    results.push({ taskId: 5, passed });
  });

  return results;
}
